import { Input } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import {
  EditorField,
  SettingToggle,
  SingleSelect,
} from './WorkQueueEditorParts';
import { createRuleId, parseNumberInput } from './work-queue-editor-utils';
import {
  WORK_QUEUE_CLAIM_ORDER_LABELS,
  WORK_QUEUE_ROUTING_METHOD_LABELS,
  WORK_QUEUE_RULE_OPERATOR_LABELS,
  type WorkQueueClaimOrder,
  type WorkQueueMember,
  type WorkQueueRoutingMethod,
  type WorkQueueRoutingOptions,
  type WorkQueueRoutingRule,
  type WorkQueueRuleOperator,
} from './work-queues-types';

const OPERATOR_OPTIONS = Object.entries(WORK_QUEUE_RULE_OPERATOR_LABELS).map(
  ([key, label]) => ({ key, label })
);
const CLAIM_ORDER_OPTIONS = Object.entries(WORK_QUEUE_CLAIM_ORDER_LABELS).map(
  ([key, label]) => ({ key, label })
);

type RoutingMethodOptionsProps = Readonly<{
  method: WorkQueueRoutingMethod;
  options: WorkQueueRoutingOptions;
  onChange: (patch: Partial<WorkQueueRoutingOptions>) => void;
  routingFields: string[];
  members: WorkQueueMember[];
  /** Role key -> display name */
  roleOptions: { key: string; label: string }[];
}>;

function NumberField({
  id,
  label,
  value,
  onChange,
  min,
  max,
}: Readonly<{
  id: string;
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
}>) {
  return (
    <EditorField label={label} htmlFor={id}>
      <Input
        id={id}
        type="number"
        variant="bordered"
        min={min}
        max={max}
        value={value === null ? '' : String(value)}
        onValueChange={(next) => onChange(parseNumberInput(next))}
        classNames={{ input: 'tabular-nums' }}
      />
    </EditorField>
  );
}

function RulesTable({
  rules,
  routingFields,
  members,
  onChange,
}: Readonly<{
  rules: WorkQueueRoutingRule[];
  routingFields: string[];
  members: WorkQueueMember[];
  onChange: (rules: WorkQueueRoutingRule[]) => void;
}>) {
  const fieldOptions = routingFields.map((field) => ({
    key: field,
    label: field,
  }));
  const memberOptions = members.map((member) => ({
    key: member.user_id,
    label: member.name,
  }));

  const updateRule = (index: number, patch: Partial<WorkQueueRoutingRule>) => {
    onChange(
      rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule))
    );
  };

  const addRule = () => {
    onChange([
      ...rules,
      {
        rule_id: createRuleId(),
        field: routingFields[0] ?? '',
        operator: 'is',
        value: '',
        assign_to: members[0]?.user_id ?? '',
      },
    ]);
  };

  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-default-200">
        <table className="w-full min-w-[620px] border-collapse text-sm">
          <thead>
            <tr className="bg-default-50 text-left">
              <th className="px-2.5 py-2 font-semibold">When</th>
              <th className="px-2.5 py-2 font-semibold w-[140px]">
                <span className="sr-only">Operator</span>
              </th>
              <th className="px-2.5 py-2 font-semibold">Value</th>
              <th className="px-2.5 py-2 font-semibold">Assign to</th>
              <th className="px-2.5 py-2 w-[52px]">
                <span className="sr-only">Remove</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-2.5 py-4 text-center text-default-500 border-t border-default-200"
                >
                  No rules yet.
                </td>
              </tr>
            )}
            {rules.map((rule, index) => (
              <tr
                key={rule.rule_id}
                className="border-t border-default-200 align-middle"
              >
                <td className="px-2.5 py-2">
                  <SingleSelect
                    size="sm"
                    ariaLabel="Field"
                    options={fieldOptions}
                    value={rule.field}
                    onChange={(field) => updateRule(index, { field })}
                  />
                </td>
                <td className="px-2.5 py-2">
                  <SingleSelect
                    size="sm"
                    ariaLabel="Operator"
                    options={OPERATOR_OPTIONS}
                    value={rule.operator}
                    onChange={(operator) =>
                      updateRule(index, {
                        operator: operator as WorkQueueRuleOperator,
                      })
                    }
                  />
                </td>
                <td className="px-2.5 py-2">
                  <Input
                    size="sm"
                    variant="bordered"
                    aria-label="Value"
                    placeholder="e.g. AllstateMech"
                    value={rule.value}
                    onValueChange={(value) => updateRule(index, { value })}
                  />
                </td>
                <td className="px-2.5 py-2">
                  <SingleSelect
                    size="sm"
                    ariaLabel="Assign to"
                    placeholder={
                      members.length ? 'Select member' : 'No members selected'
                    }
                    options={memberOptions}
                    value={rule.assign_to}
                    onChange={(assign_to) => updateRule(index, { assign_to })}
                  />
                </td>
                <td className="px-2.5 py-2">
                  <button
                    type="button"
                    onClick={() =>
                      onChange(rules.filter((_, i) => i !== index))
                    }
                    className="p-2 hover:bg-danger-50 rounded-full"
                    aria-label="Remove rule"
                  >
                    <Icon
                      icon="hugeicons:delete-02"
                      className="w-4 h-4 text-danger"
                    />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <button
          type="button"
          onClick={addRule}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:opacity-80"
        >
          <Icon icon="mdi:plus" width={16} height={16} />
          Add rule
        </button>
      </div>
      <p className="text-[12.5px] text-default-500">
        Items that match no rule fall back to load-balanced assignment.
      </p>
    </>
  );
}

function TerritoriesTable({
  territories,
  members,
  onChange,
}: Readonly<{
  territories: Record<string, string>;
  members: WorkQueueMember[];
  onChange: (territories: Record<string, string>) => void;
}>) {
  return (
    <div className="overflow-x-auto rounded-lg border border-default-200">
      <table className="w-full min-w-[520px] border-collapse text-sm">
        <thead>
          <tr className="bg-default-50 text-left">
            <th className="px-2.5 py-2 font-semibold">Member</th>
            <th className="px-2.5 py-2 font-semibold">
              Covers states or ZIP prefixes
            </th>
          </tr>
        </thead>
        <tbody>
          {members.length === 0 && (
            <tr>
              <td
                colSpan={2}
                className="px-2.5 py-4 text-default-500 border-t border-default-200"
              >
                Select queue members first.
              </td>
            </tr>
          )}
          {members.map((member) => (
            <tr key={member.user_id} className="border-t border-default-200">
              <td className="px-2.5 py-2 whitespace-nowrap">{member.name}</td>
              <td className="px-2.5 py-2">
                <Input
                  size="sm"
                  variant="bordered"
                  aria-label={`Territory for ${member.name}`}
                  placeholder="e.g. FL, GA, 335"
                  value={territories[member.user_id] ?? ''}
                  onValueChange={(value) =>
                    onChange({ ...territories, [member.user_id]: value })
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderMethodSettings({
  method,
  options,
  onChange,
  routingFields,
  members,
  roleOptions,
}: RoutingMethodOptionsProps) {
  switch (method) {
    case 'manual':
      return (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EditorField
              label="Who assigns new items"
              htmlFor="wq-assigner-role"
            >
              <SingleSelect
                id="wq-assigner-role"
                ariaLabel="Who assigns new items"
                placeholder="Select a role"
                options={roleOptions}
                value={options.assigner_role}
                onChange={(assigner_role) => onChange({ assigner_role })}
              />
            </EditorField>
            <NumberField
              id="wq-unassigned-hours"
              label="Alert if unassigned after (hours)"
              min={1}
              value={options.unassigned_alert_hours}
              onChange={(unassigned_alert_hours) =>
                onChange({ unassigned_alert_hours })
              }
            />
          </div>
          <div>
            <SettingToggle
              title="Let members assign items to themselves"
              description="Members can pick up unassigned items without a dispatcher"
              isSelected={options.allow_self_assign}
              onValueChange={(allow_self_assign) =>
                onChange({ allow_self_assign })
              }
            />
            <SettingToggle
              title="Notify the assignee"
              description="Email and PAL notification when an item is assigned"
              isSelected={options.notify_assignee}
              onValueChange={(notify_assignee) => onChange({ notify_assignee })}
            />
          </div>
        </>
      );
    case 'round_robin':
      return (
        <SettingToggle
          title="Skip members who are out of office"
          description="Their turn passes to the next member"
          isSelected={options.skip_out_of_office}
          onValueChange={(skip_out_of_office) =>
            onChange({ skip_out_of_office })
          }
        />
      );
    case 'load_balanced':
      return (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <NumberField
              id="wq-max-open"
              label="Max open items per member"
              min={1}
              value={options.max_open_per_member}
              onChange={(max_open_per_member) =>
                onChange({ max_open_per_member })
              }
            />
          </div>
          <SettingToggle
            title="Skip members who are out of office"
            isSelected={options.skip_out_of_office}
            onValueChange={(skip_out_of_office) =>
              onChange({ skip_out_of_office })
            }
          />
        </>
      );
    case 'rules_based':
      return (
        <RulesTable
          rules={options.rules}
          routingFields={routingFields}
          members={members}
          onChange={(rules) => onChange({ rules })}
        />
      );
    case 'territory':
      return (
        <TerritoriesTable
          territories={options.territories}
          members={members}
          onChange={(territories) => onChange({ territories })}
        />
      );
    case 'shared_pull':
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <EditorField
            label="Members claim items in this order"
            htmlFor="wq-claim-order"
          >
            <SingleSelect
              id="wq-claim-order"
              ariaLabel="Members claim items in this order"
              options={CLAIM_ORDER_OPTIONS}
              value={options.claim_order}
              onChange={(claim_order) =>
                onChange({ claim_order: claim_order as WorkQueueClaimOrder })
              }
            />
          </EditorField>
          <NumberField
            id="wq-release-minutes"
            label="Release a claimed item after (minutes idle)"
            min={1}
            value={options.release_after_idle_minutes}
            onChange={(release_after_idle_minutes) =>
              onChange({ release_after_idle_minutes })
            }
          />
        </div>
      );
    case 'ai_recommended':
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <NumberField
            id="wq-min-confidence"
            label="Auto-assign when confidence is at least (%)"
            min={50}
            max={100}
            value={options.min_confidence_percent}
            onChange={(min_confidence_percent) =>
              onChange({ min_confidence_percent })
            }
          />
          <EditorField
            label="Below that, send to"
            htmlFor="wq-ai-fallback-role"
          >
            <SingleSelect
              id="wq-ai-fallback-role"
              ariaLabel="Below that, send to"
              placeholder="Select a role"
              options={roleOptions}
              value={options.assigner_role}
              onChange={(assigner_role) => onChange({ assigner_role })}
            />
          </EditorField>
        </div>
      );
    default:
      return null;
  }
}

export function RoutingMethodOptions(props: RoutingMethodOptionsProps) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-medium text-foreground">
        {WORK_QUEUE_ROUTING_METHOD_LABELS[props.method]} settings
      </p>
      {renderMethodSettings(props)}
    </div>
  );
}

export default RoutingMethodOptions;
